import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ShieldCheck } from "lucide-react";
import { useSignedUrl } from "@/hooks/useSignedUrl";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title?: string;
  bucket?: string;
}

function isPdf(url: string) {
  return /\.pdf(\?|#|$)/i.test(url);
}

function isImage(url: string) {
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(url);
}

export function DocumentViewerModal({ open, onOpenChange, url, title, bucket }: Props) {
  const { secureUrl, loading } = useSignedUrl(open ? url : null, bucket);

  const handleDownload = () => {
    if (!secureUrl) return;
    const link = document.createElement("a");
    link.href = secureUrl;
    link.download = title || "documento";
    link.target = "_blank";
    link.click();
  };

  // Determine the source URL to check for type — use original url for extension detection
  const sourceForType = url || "";
  const pdfViewerUrl = secureUrl && isPdf(sourceForType)
    ? `https://docs.google.com/gview?url=${encodeURIComponent(secureUrl)}&embedded=true`
    : secureUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0 flex flex-row items-center justify-between">
          <DialogTitle className="text-sm truncate">{title || "Documento"}</DialogTitle>
          <Button variant="outline" size="sm" className="gap-2 shrink-0 mr-8" onClick={handleDownload} disabled={loading || !secureUrl}>
            <Download className="h-4 w-4" />
            Descargar
          </Button>
        </DialogHeader>
        <div className="flex-1 min-h-0 p-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
              <ShieldCheck className="h-10 w-10 animate-pulse text-primary" />
              <span className="text-sm">Autenticando documento seguro…</span>
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !secureUrl ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              No se pudo cargar el documento.
            </div>
          ) : isPdf(sourceForType) ? (
            <iframe src={pdfViewerUrl!} title={title || "Documento"} className="w-full h-full rounded border-0" />
          ) : isImage(sourceForType) ? (
            <img src={secureUrl} alt={title || "Documento"} className="w-full h-full object-contain rounded" />
          ) : (
            <iframe src={secureUrl} title={title || "Documento"} className="w-full h-full rounded border-0" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
